import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { 
  BarChart3, TrendingUp, Package, DollarSign, Calendar, Download, 
  Loader2, ChevronDown, ChevronRight, Building2, Tag, Filter, Store
} from 'lucide-react';
import { ShareSummaryButton } from '../components/ShareSummaryButton';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SalesReport = () => {
  const { getAuthHeader } = useAuth();
  const { selectedStore, stores, storeName } = useStore();
  
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState('supplier');
  const [supplierReport, setSupplierReport] = useState(null);
  const [brandReport, setBrandReport] = useState(null);
  const [discountReport, setDiscountReport] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  
  // Filters
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [storeFilter, setStoreFilter] = useState('all');
  const [brandFilter, setBrandFilter] = useState(''); // empty = all brands
  const [suppliers, setSuppliers] = useState([]);
  const [availableBrands, setAvailableBrands] = useState([]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/suppliers`, { headers: getAuthHeader() });
      setSuppliers(response.data);
    } catch (error) {
      console.error('Failed to load suppliers');
    }
  }, [getAuthHeader]);

  const fetchSupplierReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate
      });
      if (storeFilter !== 'all') params.append('store', storeFilter);
      
      const response = await axios.get(`${API_URL}/reports/sales-by-supplier?${params}`, {
        headers: getAuthHeader()
      });
      setSupplierReport(response.data);
    } catch (error) {
      toast.error('Failed to load supplier report');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, startDate, endDate, storeFilter]);

  const fetchBrandReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate
      });
      if (storeFilter !== 'all') params.append('store', storeFilter);
      if (brandFilter) params.append('brand', brandFilter);
      
      const response = await axios.get(`${API_URL}/reports/sales-by-brand?${params}`, {
        headers: getAuthHeader()
      });
      setBrandReport(response.data);
      // Keep the full list of brands cached separately for the dropdown.
      // Only refresh it when no filter is applied so we don't lose options.
      if (!brandFilter && Array.isArray(response.data?.brands)) {
        const names = response.data.brands.map(b => b.brand).filter(Boolean).sort((a, b) => a.localeCompare(b));
        setAvailableBrands(names);
      }
    } catch (error) {
      toast.error('Failed to load brand report');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, startDate, endDate, storeFilter, brandFilter]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const fetchDiscountReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      if (storeFilter !== 'all') params.append('store', storeFilter);
      const response = await axios.get(`${API_URL}/reports/discounts?${params}`, {
        headers: getAuthHeader()
      });
      setDiscountReport(response.data);
    } catch (error) {
      toast.error('Failed to load discounts report');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, startDate, endDate, storeFilter]);

  useEffect(() => {
    if (reportType === 'supplier') {
      fetchSupplierReport();
    } else if (reportType === 'discounts') {
      fetchDiscountReport();
    } else {
      fetchBrandReport();
    }
  }, [reportType, fetchSupplierReport, fetchBrandReport, fetchDiscountReport]);

  const toggleRow = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const exportToCSV = () => {
    if (reportType === 'discounts') {
      if (!discountReport) return;
      let csv = 'Date,Staff,Store,Type,Product,Reason,Amount\n';
      discountReport.entries.forEach(e => {
        csv += `"${e.date}","${e.staff_name}","${e.store}","${e.type}","${e.product_name}","${e.reason}",${e.amount.toFixed(2)}\n`;
      });
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `discounts-report-${startDate}-to-${endDate}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report exported to CSV');
      return;
    }
    const report = reportType === 'supplier' ? supplierReport : brandReport;
    if (!report) return;
    
    let csv = 'Category,Product Name,SKU,Units Sold,Revenue\n';
    
    if (reportType === 'supplier') {
      report.suppliers.forEach(supplier => {
        supplier.products.forEach(product => {
          csv += `"${supplier.supplier_name}","${product.product_name}","${product.sku}",${product.units_sold},${product.revenue.toFixed(2)}\n`;
        });
      });
    } else {
      report.brands.forEach(brand => {
        brand.products.forEach(product => {
          csv += `"${brand.brand}","${product.product_name}","${product.sku}",${product.units_sold},${product.revenue.toFixed(2)}\n`;
        });
      });
    }
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales-report-${reportType}-${startDate}-to-${endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report exported to CSV');
  };

  const report = reportType === 'supplier' ? supplierReport : reportType === 'brand' ? brandReport : discountReport;
  const items = reportType === 'supplier' ? report?.suppliers : report?.brands;

  return (
    <div className="space-y-6" data-testid="sales-report-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-heading text-2xl font-bold uppercase tracking-tight flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-emerald-600" />
            Sales Report
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Sales breakdown by supplier or brand for staff incentives
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ShareSummaryButton />
          <Button 
            onClick={exportToCSV}
            disabled={!report}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="w-4 h-4" /> Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Store</Label>
              <Select value={storeFilter} onValueChange={setStoreFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stores</SelectItem>
                  <SelectItem value="orewa">{storeName('orewa')}</SelectItem>
                  <SelectItem value="eastridge">{storeName('eastridge')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Group By</Label>
              <Select value={reportType} onValueChange={(v) => { setReportType(v); if (v !== 'brand') setBrandFilter(''); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="supplier">Supplier</SelectItem>
                  <SelectItem value="brand">Brand</SelectItem>
                  <SelectItem value="discounts" data-testid="report-type-discounts">Discounts</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {reportType === 'brand' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t">
              <div className="space-y-2">
                <Label>Filter by Brand (optional)</Label>
                <Select value={brandFilter || '__all__'} onValueChange={(v) => setBrandFilter(v === '__all__' ? '' : v)}>
                  <SelectTrigger data-testid="brand-filter-select">
                    <SelectValue placeholder="All brands" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__all__">All brands</SelectItem>
                    {availableBrands.map(b => (
                      <SelectItem key={b} value={b} data-testid={`brand-filter-option-${b}`}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400">
                  Tip: Pick a brand to see only that brand's sales for the selected period (great for incentives like Dr Hauschka).
                </p>
              </div>
              {brandFilter && (
                <div className="flex items-end">
                  <Button variant="outline" size="sm" onClick={() => setBrandFilter('')} data-testid="brand-filter-clear">
                    Clear brand filter
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {report && reportType !== 'discounts' && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Period</p>
                  <p className="text-lg font-bold">
                    {formatDate(startDate)} - {formatDate(endDate)}
                  </p>
                </div>
                <Calendar className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">
                    {reportType === 'supplier' ? 'Total Suppliers' : 'Total Brands'}
                  </p>
                  <p className="text-2xl font-bold">
                    {reportType === 'supplier' ? report.summary.total_suppliers : report.summary.total_brands}
                  </p>
                </div>
                {reportType === 'supplier' ? (
                  <Building2 className="w-8 h-8 text-purple-500" />
                ) : (
                  <Tag className="w-8 h-8 text-purple-500" />
                )}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Units Sold</p>
                  <p className="text-2xl font-bold">{report.summary.total_units_sold}</p>
                </div>
                <Package className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Revenue</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    ${report.summary.total_revenue.toFixed(2)}
                  </p>
                </div>
                <DollarSign className="w-8 h-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Report Table */}
      {reportType !== 'discounts' && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {reportType === 'supplier' ? (
              <><Building2 className="w-5 h-5 text-purple-500" /> Sales by Supplier</>
            ) : (
              <><Tag className="w-5 h-5 text-purple-500" /> Sales by Brand</>
            )}
          </CardTitle>
          <CardDescription>
            Click on a row to see individual product details
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
          ) : !items || items.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No sales data for this period</p>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((item, index) => {
                const itemId = reportType === 'supplier' ? item.supplier_id : item.brand;
                const isExpanded = expandedRows[itemId];
                
                return (
                  <div key={itemId} className="border rounded-lg overflow-hidden">
                    {/* Summary Row */}
                    <div 
                      className="flex items-center justify-between p-4 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => toggleRow(itemId)}
                    >
                      <div className="flex items-center gap-3">
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        )}
                        <div>
                          <p className="font-bold text-lg">
                            {reportType === 'supplier' ? item.supplier_name : item.brand}
                          </p>
                          <p className="text-sm text-slate-500">
                            {item.product_count} product{item.product_count !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-sm text-slate-500">Units Sold</p>
                          <p className="font-bold text-lg">{item.total_units}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-500">Revenue</p>
                          <p className="font-bold text-lg text-emerald-600">${item.total_revenue.toFixed(2)}</p>
                        </div>
                        <Badge className="bg-blue-100 text-blue-800">
                          #{index + 1}
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Product Details */}
                    {isExpanded && (
                      <div className="border-t">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50/50">
                              <TableHead>Product Name</TableHead>
                              <TableHead>SKU</TableHead>
                              {reportType === 'supplier' && <TableHead>Brand</TableHead>}
                              <TableHead className="text-right">Units Sold</TableHead>
                              <TableHead className="text-right">Revenue</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {item.products.map((product) => (
                              <TableRow key={product.product_id}>
                                <TableCell className="font-medium">{product.product_name}</TableCell>
                                <TableCell className="font-mono text-sm">{product.sku || '-'}</TableCell>
                                {reportType === 'supplier' && (
                                  <TableCell>{product.brand || '-'}</TableCell>
                                )}
                                <TableCell className="text-right">
                                  <Badge variant="outline">{product.units_sold}</Badge>
                                </TableCell>
                                <TableCell className="text-right font-bold text-emerald-600">
                                  ${product.revenue.toFixed(2)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Discounts Report */}
      {reportType === 'discounts' && (
        <div className="space-y-6" data-testid="discounts-report">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
          ) : !discountReport ? null : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Total Discounted</p>
                        <p className="text-2xl font-bold text-rose-600" data-testid="total-discounted">
                          -${discountReport.summary.total_discounted.toFixed(2)}
                        </p>
                      </div>
                      <DollarSign className="w-8 h-8 text-rose-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Discounts Given</p>
                        <p className="text-2xl font-bold">{discountReport.summary.discount_count}</p>
                      </div>
                      <Tag className="w-8 h-8 text-purple-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Top Reason</p>
                        <p className="text-lg font-bold">
                          {discountReport.summary.by_reason[0]?.reason || '—'}
                        </p>
                      </div>
                      <BarChart3 className="w-8 h-8 text-amber-500" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Tag className="w-5 h-5 text-rose-500" /> Margin Loss by Reason
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {discountReport.summary.by_reason.length === 0 ? (
                    <p className="text-center py-8 text-slate-500">No discounts in this period</p>
                  ) : (
                    <div className="space-y-2">
                      {discountReport.summary.by_reason.map(r => (
                        <div key={r.reason} className="flex items-center justify-between p-3 bg-slate-50 rounded border" data-testid={`reason-row-${r.reason}`}>
                          <div className="flex items-center gap-3">
                            <Badge className="bg-rose-100 text-rose-800">{r.count}×</Badge>
                            <span className="font-medium">{r.reason}</span>
                          </div>
                          <span className="font-bold text-rose-600">-${r.total.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Filter className="w-5 h-5 text-slate-500" /> Discount Details
                  </CardTitle>
                  <CardDescription>Every discount given in the period, most recent first</CardDescription>
                </CardHeader>
                <CardContent>
                  {discountReport.entries.length === 0 ? (
                    <p className="text-center py-8 text-slate-500">No discounts in this period</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50/50">
                          <TableHead>Date</TableHead>
                          <TableHead>Staff</TableHead>
                          <TableHead>Store</TableHead>
                          <TableHead>Product / Sale</TableHead>
                          <TableHead>Reason</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {discountReport.entries.map((e, i) => (
                          <TableRow key={`${e.sale_id}-${i}`}>
                            <TableCell className="text-sm whitespace-nowrap">{formatDateTime(e.date)}</TableCell>
                            <TableCell>{e.staff_name || '-'}</TableCell>
                            <TableCell className="capitalize">{e.store}</TableCell>
                            <TableCell className="font-medium">
                              {e.product_name}
                              {e.type === 'cart' && <Badge variant="outline" className="ml-2 text-xs">Whole cart</Badge>}
                            </TableCell>
                            <TableCell><Badge className="bg-amber-100 text-amber-800">{e.reason}</Badge></TableCell>
                            <TableCell className="text-right font-bold text-rose-600">-${e.amount.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SalesReport;
